import type { TFunction } from "i18next";

type Language = "nb" | "en";

export interface IProvider {
  link: string;
  url: string;
}

export interface IProviderWithTitle extends IProvider {
  title: string;
}

export interface IHighlights {
  url: string;
}

/**
 * SCOUT_PROVIDERS contains RSS urls used in the ScoutProvider component.
 * Rendered on the Home and Scout page.
 */
export const getScoutProviders = (t: TFunction): IProviderWithTitle[] => {
  return [
    {
      url: "https://www.tv2.no/rest/cms-feeds-dw-rest/v2/cms/article/sport/fotball/fantasy",
      link: "https://www.tv2.no/sport/fotball/eliteserien",
      title: t("feed.providers.title1", "Latest Fantasy-news from TV 2"),
    },
    {
      url: "https://www.eliteserien.no/rss-nyheter",
      link: "https://www.eliteserien.no/nyheter",
      title: t("feed.providers.title2", "Latest news from Eliteserien"),
    },
  ];
};

/**
 * HERO_PROVIDERS contains RSS urls used in the HeroProvider component.
 * Rendered on the Home page.
 */
export const getHeroProviders = (): Record<Language, IProvider[]> => {
  return {
    nb: [
      {
        url: "https://www.eliteserien.no/fantasyfrontpage",
        link: "https://www.eliteserien.no/fantasyfrontpage",
      },
    ],
    en: [
      {
        url: "https://www.eliteserien.no/fantasyfrontpageenglish",
        link: "https://www.eliteserien.no/fantasyfrontpageenglish",
      },
    ],
  };
};

/**
 * HIGHLIGHTS contains API urls used in the video HighlightsProvider component.
 * Rendered on the Home page and ElementDialog.
 */
/**
 * Season IDs for TV 2 highlights content
SEASON_2025_ID = "e53e02b8-a512-51d3-aa98-ceda8e3a01b1";
SEASON_2026_ID = "0ef27b0a-2067-505f-ac53-f2bb61f65918";
 */
const PREPROD_HOME =
  "https://tv2news.content-discovery.cf.eu-north-1-preprod.vmnd.tv/api/v1/assets?platform=web&page%5bsize%5d=10&page%5bnumber%5d=1&sort=-event-occurrence-time&query=%28%28video-type%3aSportAutomatic%29%20and%20%28tv2-tournament-id%3a%220ef27b0a-2067-505f-ac53-f2bb61f65918%22%20or%20tv2-tournament-id%3a%22e53e02b8-a512-51d3-aa98-ceda8e3a01b1%22%29%20and%20%28type-of-action%3agoal%20or%20type-of-action%3aredcard%20or%20type-of-action%3ahighlight%29%20and%20assetType%3a%229%3a16%22%29";

const PROD_HOME =
  "https://tv2news.content-discovery.cf.eu-north-1-prod.vmnd.tv/api/v1/assets?platform=web&page%5bsize%5d=10&page%5bnumber%5d=1&sort=-event-occurrence-time&query=%28%28video-type%3aSportAutomatic%29%20and%20%28tv2-tournament-id%3a%220ef27b0a-2067-505f-ac53-f2bb61f65918%22%20or%20tv2-tournament-id%3a%22e53e02b8-a512-51d3-aa98-ceda8e3a01b1%22%29%20and%20%28type-of-action%3agoal%20or%20type-of-action%3aredcard%20or%20type-of-action%3ahighlight%29%20and%20assetType%3a%229%3a16%22%29";

const PREPROD_ELEMENT = (elementFiksId: number) =>
  `https://tv2news.content-discovery.cf.eu-north-1-preprod.vmnd.tv/api/v1/assets?platform=web&page%5bsize%5d=5&page%5bnumber%5d=1&query=%28fiks-id-player%3a%22person-${elementFiksId}%22%20and%20%28type-of-action%3agoal%20or%20type-of-action%3aredcard%20or%20type-of-action%3ayellowcard%20or%20type-of-action%3achance%20or%20type-of-action%3asaves%20or%20type-of-action%3aassist%20or%20type-of-action%3adefensiveact%29%20and%20%28tv2-tournament-id%3a%220ef27b0a-2067-505f-ac53-f2bb61f65918%22%20or%20tv2-tournament-id%3a%22e53e02b8-a512-51d3-aa98-ceda8e3a01b1%22%29%20and%20assetType%3a%229%3a16%22%29`;

const PROD_ELEMENT = (elementFiksId: number) =>
  `https://tv2news.content-discovery.cf.eu-north-1-prod.vmnd.tv/api/v1/assets?platform=web&page%5bsize%5d=5&page%5bnumber%5d=1&query=%28fiks-id-player%3a%22person-${elementFiksId}%22%20and%20%28type-of-action%3agoal%20or%20type-of-action%3aredcard%20or%20type-of-action%3ayellowcard%20or%20type-of-action%3achance%20or%20type-of-action%3asaves%20or%20type-of-action%3aassist%20or%20type-of-action%3adefensiveact%29%20and%20%28tv2-tournament-id%3a%220ef27b0a-2067-505f-ac53-f2bb61f65918%22%20or%20tv2-tournament-id%3a%22e53e02b8-a512-51d3-aa98-ceda8e3a01b1%22%29%20and%20assetType%3a%229%3a16%22%29`;

export const getHighlightsUrl = (elementFiksId?: number) => {
  const isProd = import.meta.env.VITE_SENTRY_ENVIRONMENT === "production";

  let url: string;

  if (elementFiksId) {
    url = isProd ? PROD_ELEMENT(elementFiksId) : PREPROD_ELEMENT(elementFiksId);
  } else {
    url = isProd ? PROD_HOME : PREPROD_HOME;
  }

  const highlights: IHighlights[] = [{ url }];
  return highlights;
};
